import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { AdminService } from 'src/app/admin-pages/admin.service';
import { LayoutService } from 'src/app/layout/layout.service';
import { UtilityService } from 'src/app/services/utility.service';
import { ConfigService } from 'src/app/services/config.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-servicenear-social-profiles',
  templateUrl: './servicenear-social-profiles.component.html',
  styleUrls: ['./servicenear-social-profiles.component.scss']
})
export class ServicenearSocialProfilesComponent implements OnInit, OnDestroy {

  socialProfilesForm!: FormGroup;
  profileDetails: any = {};
  userDetails: any = {};
  isEdit: boolean = false;
  isLoading: boolean = true;
  private subscription: Subscription = new Subscription();

  // Toast notification
  notificationMsg = '';
  showToast: boolean = false;
  isSuccess: boolean = false;

  private readonly urlFields = ['facebook', 'youtube', 'instagram', 'googleBusiness', 'externalWebsite'];

  constructor(
    private fb: FormBuilder,
    public adminService: AdminService,
    public layoutService: LayoutService,
    private utilityService: UtilityService,
    private configService: ConfigService,
  ) { }

  ngOnInit(): void {
    this.populateToolbarData();
    this.userDetails = this.adminService.getLoginDetails();
    this.initForm();
    this.getSellerSettings();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private populateToolbarData(): void {
    this.layoutService.setToolbar({ title: 'Social Profiles' });
    this.layoutService.setNavbarButtons([]);
  }

  private initForm(): void {
    this.socialProfilesForm = this.fb.group({
      facebook: [''],
      youtube: [''],
      gmail: [''],
      instagram: [''],
      googleBusiness: [''],
      externalWebsite: [''],
    });
    this.socialProfilesForm.disable();
  }

  private getSellerSettings(): void {
    this.isLoading = true;
    const params = { filters: "All" };
    
    this.subscription.add(
      this.configService.getSellerSettingsBySellerId(params).subscribe(
        (res: any) => {
          if (res?.Result === 'Success' && res?.ResponseData) {
            try {
              this.profileDetails.SocialMediaLinks = JSON.parse(
                res.ResponseData.SocialMediaLinks || '{}'
              );
            } catch {
              this.profileDetails.SocialMediaLinks = {};
            }
            this.patchForm();
          }
          this.isLoading = false;
        },
        (error: HttpErrorResponse) => {
          this.isLoading = false;
          this.notify(false, 'Failed to load social profiles. Please try again.');
        }
      )
    );
  }

  private patchForm(): void {
    const links = this.profileDetails?.SocialMediaLinks || {};
    this.socialProfilesForm.patchValue({
      facebook: links.facebook || '',
      youtube: links.youtube || '',
      gmail: links.gmail || '',
      instagram: links.instagram || '',
      googleBusiness: links.googleBusiness || '',
      externalWebsite: links.externalWebsite || '',
    });
  }

  public editBtnClicked(): void {
    this.isEdit = true;
    this.socialProfilesForm.enable();
  }

  public cancelEdit(): void {
    this.isEdit = false;
    this.socialProfilesForm.disable();
    this.patchForm();
  }

  public submitSocialProfiles(): void {
    let allUrlsValid = true;
    const urlRegex = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/i;
    this.urlFields.forEach((field) => {
      const control = this.socialProfilesForm.get(field);
      let value = control?.value?.trim();
      if (value) {
        if (!urlRegex.test(value)) {
          control?.setErrors({ invalidUrl: true });
          allUrlsValid = false;
          return;
        }

        if (!/^https?:\/\//i.test(value)) {
          value = 'https://' + value;
        }
        const sanitized = this.utilityService.sanitizeUrl(value);
        if (!sanitized) {
          control?.setErrors({ invalidUrl: true });
          allUrlsValid = false;
        } else {
          control?.setValue(sanitized, { emitEvent: false });
          control?.setErrors(null);
        }
      } else {
        control?.setErrors(null);
      }
    });

    if (!allUrlsValid) {
      this.notify(false, 'Please check the social media links.');
      return;
    }

    this.isLoading = true;
    const sellerDetails = this.configService?.sellerConfiguration || {};
    const existingSocialMediaLinks = sellerDetails?.SocialMediaLinks || {};
    const params = {
      sellerName: sellerDetails.SellerName || '',
      customerCare: sellerDetails.CustomerCare || '',
      district: sellerDetails.City || '',
      address: sellerDetails.Address || '',
      gstNumber: sellerDetails.GSTNumber || '',
      addressLine1: sellerDetails.Addressline1 || '',
      state: sellerDetails.State || '',
      country: sellerDetails.Country || '',
      pincode: sellerDetails.Pincode || '',

      facebook: this.socialProfilesForm.value.facebook || '',
      youtube: this.socialProfilesForm.value.youtube || '',
      gmail: this.socialProfilesForm.value.gmail || '',
      customerCareGmail: existingSocialMediaLinks.customerCareGmail || '',
      instagram: this.socialProfilesForm.value.instagram || '',
      googleBusiness: this.socialProfilesForm.value.googleBusiness || '',
      externalWebsite: this.socialProfilesForm.value.externalWebsite || '',
    };

    this.subscription.add(
      this.adminService.updateSellerProfile(params).subscribe(
        (res: any) => {
          if (res?.Result === 'Success') {
            this.configService.getSettingsBySellerId();
            this.getSellerSettings();
            this.isEdit = false;
            this.socialProfilesForm.disable();
            this.notify(true, 'Social profiles updated successfully.');
          } else {
            this.notify(false, 'Failed to update. Please try again.');
          }
          this.isLoading = false;
        },
        (error: HttpErrorResponse) => {
          this.isLoading = false;
          this.notify(false, 'Failed to update. Please try again.');
        }
      )
    );
  }

  private notify(success: boolean, msg: string): void {
    this.showToast = true;
    this.isSuccess = success;
    this.notificationMsg = msg;
    setTimeout(() => {
      this.showToast = false;
    }, 3000);
  }
}
